import { ReactNode, Suspense, lazy } from 'react'
import {
  AiOutlineDashboard,
  AiOutlineAppstore,
  AiOutlineUser,
  AiOutlineSnippets,
  AiOutlineSetting,
  AiOutlineShopping,
  AiOutlineShop,
  AiOutlineTool,
  AiOutlineContainer,
} from 'react-icons/ai'
import { RiCoupon2Line } from 'react-icons/ri'
import { Navigate } from 'react-router-dom'

const Dashboard = lazy(() => import('./pages/Dashboard/index'))
const Onboarding = lazy(() => import('./pages/OnBoarding/index'))

const AccountManagement = lazy(
  () => import('./pages/Account/AccountManagement/index'),
)
const AccountLogoTitle = lazy(
  () => import('./pages/Account/AccountLogoTitle/index'),
)
const AddAccount = lazy(() => import('./pages/Account/AddAccount/index'))
const EditAccount = lazy(() => import('./pages/Account/EditAccount/index'))
const AccountInfo = lazy(() => import('./pages/Account/AccountInfo/index'))

const StoreInfo = lazy(() => import('./pages/Stores/StoreInfo/index'))
const Stores = lazy(() => import('./pages/Stores/index'))

const Products = lazy(() => import('./pages/Products/index'))
const ProductDetail = lazy(() => import('./pages/Products/Detail'))

const StoreLogs = lazy(() => import('./pages/Reports/StoreLogs/index'))
const Reports = lazy(() => import('./pages/Reports/Home/index'))
const PlanRevenue = lazy(() => import('./pages/Reports/PlanRevenue/index'))
const PortalLogs = lazy(() => import('./pages/Reports/PortalLogs/index'))
const SystemLogs = lazy(() => import('./pages/Reports/SystemLogs/index'))
const LatestPlanChanges = lazy(
  () => import('./pages/Reports/LatestPlanChanges/index'),
)
const PromotionUtilization = lazy(
  () => import('./pages/Reports/PromotionUtilization/index'),
)
const OrdersRevenue = lazy(() => import('./pages/Reports/OrderRevenue/index'))

const Settings = lazy(() => import('./pages/Settings/Home/index'))
const PortalUser = lazy(() => import('./pages/Settings/PortalUser/index'))
const Plan = lazy(() => import('./pages/Settings/Plan/index'))
const Group = lazy(() => import('./pages/Settings/Group/index'))
const AutomationNotification = lazy(
  () => import('./pages/Settings/AutomationNotification/index'),
)
const APIFranchiseToken = lazy(
  () => import('./pages/Settings/APIFranchiseToken/index'),
)
const SMSGatewayConfig = lazy(
  () => import('./pages/Settings/SMSGatewayConfig/index'),
)
const SMSTemplate = lazy(() => import('./pages/Settings/SMSTemplate/index'))
const SMTPSetting = lazy(() => import('./pages/Settings/SMTPSetting/index'))
const MailTemplate = lazy(() => import('./pages/Settings/MailTemplate/index'))
const MasterStore = lazy(() => import('./pages/Settings/MasterStore/index'))
const CatalogSetting = lazy(
  () => import('./pages/Settings/CatalogSetting/index'),
)

const PromotionList = lazy(
  () => import('./pages/Promotions/PromotionList/index'),
)
const PromotionAddition = lazy(
  () => import('./pages/Promotions/PromotionAddition/index'),
)
const PromotionEdition = lazy(
  () => import('./pages/Promotions/PromotionEdition/index'),
)
const PushPromotions = lazy(
  () => import('./pages/Promotions/PushPromotions/index'),
)
const AddOrEditShopifyDiscountPage = lazy(
  () => import('./pages/Promotions/AddOrEditShopifyPromotion/index'),
)

const ThemesManagement = lazy(
  () => import('./pages/Themes/ThemesManagement/index'),
)
const ThemeApply = lazy(() => import('./pages/Themes/ThemeApply/index'))
const ThemeUpload = lazy(() => import('./pages/Themes/ThemeUpload/index'))

const CustomerManagement = lazy(
  () => import('./pages/CustomerProfiles/CustomerManagement/index'),
)
const CustomerProfileDetails = lazy(
  () => import('./pages/CustomerProfiles/CustomerProfileDetails/index'),
)
const CustomerOrderRevenue = lazy(
  () => import('./pages/CustomerProfiles/CustomerOrderRevenue/index'),
)

const ProvisioningProfiles = lazy(
  () => import('./pages/ProvisioningProfiles/ProfilesManagement/index'),
)
const ProfileDetails = lazy(
  () => import('./pages/ProvisioningProfiles/ProfileDetails/index'),
)
const ProfileShopifyDetails = lazy(
  () =>
    import('./pages/ProvisioningProfiles/ShopifyProvisioningProfiles/Detail'),
)
const ProfileShopifyCreate = lazy(
  () =>
    import('./pages/ProvisioningProfiles/ShopifyProvisioningProfiles/Create'),
)
const ProfileShopifyEdit = lazy(
  () => import('./pages/ProvisioningProfiles/ShopifyProvisioningProfiles/Edit'),
)
const ApplyProfile = lazy(
  () => import('./pages/ProvisioningProfiles/ApplyProfile/index'),
)
const EditProfile = lazy(
  () => import('./pages/ProvisioningProfiles/EditProfile/index'),
)
const CreateProfile = lazy(
  () => import('./pages/ProvisioningProfiles/CreateProfile/index'),
)

const Orders = lazy(() => import('./pages/Orders/index'))
const OrderDetail = lazy(() => import('./pages/OrderDetail/index'))

const ResetPassword = lazy(() => import('./pages/ResetPassword/index'))
const Login = lazy(() => import('./pages/Login/index'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword/index'))

const Content = lazy(() => import('./pages/Content/index'))

const ContentForm = lazy(() => import('./pages/Content/ContentForm'))

export interface RouteItem {
  id: string
  element: ReactNode
  path: string
  isPublic?: boolean
  text?: string
  icon?: ReactNode
  permissions?: string[]
}

export const routes: Array<RouteItem> = [
  {
    id: 'login',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <Login />
      </Suspense>
    ),
    path: 'login',
    isPublic: true,
  },
  {
    id: 'forgot-password',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ForgotPassword />
      </Suspense>
    ),
    path: 'forgot-password',
    isPublic: true,
  },
  {
    id: 'reset-password',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ResetPassword />
      </Suspense>
    ),
    path: 'reset-password/:token',
    isPublic: true,
  },
  {
    id: 'add-account-logo-title',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <AccountLogoTitle />
      </Suspense>
    ),
    path: 'add-account-logo-title',
    isPublic: true,
  },
  {
    id: 'dashboard',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <Dashboard />
      </Suspense>
    ),
    path: 'dashboard',
    text: 'layout_route_text_dashboard',
    icon: <AiOutlineDashboard size={18} />,
    permissions: [
      'revenue.get',
      'accounts.get',
      'tenant_logs.get',
      'plan-payments.get',
      'plan-changes.get',
      'users.get',
      'plans.get',
      'groups.get',
      'configs.id.get',
    ],
  },
  {
    id: 'stores',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <Stores />
      </Suspense>
    ),
    path: 'stores',
    text: 'layout_route_text_stores',
    permissions: ['stores.get'],
    icon: <AiOutlineAppstore size={18} />,
  },
  {
    id: 'stores/store-info',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <StoreInfo />
      </Suspense>
    ),
    permissions: ['stores.id.get'],
    path: 'stores/store-info/:id',
  },
  {
    id: 'account',
    text: 'layout_route_text_account',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <AccountManagement />
      </Suspense>
    ),
    path: 'account',
    icon: <AiOutlineUser size={18} />,
    permissions: ['accounts.get'],
  },
  {
    id: 'account/add-account',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <AddAccount />
      </Suspense>
    ),
    path: 'account/add-account',
    permissions: ['accounts.post'],
  },
  {
    id: 'account/edit-account',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <EditAccount />
      </Suspense>
    ),
    path: 'account/edit-account',
    permissions: ['accounts.id.put', 'accounts.id.patch'],
  },
  {
    id: 'account/account-info',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <AccountInfo />
      </Suspense>
    ),
    path: 'account/account-info/:id',
    permissions: ['accounts.id.get'],
  },
  {
    id: 'content',
    text: 'layout_route_text_content',
    path: 'content',
    icon: <AiOutlineSetting size={18} />,
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <Content />
      </Suspense>
    ),
    permissions: ['content.get'],
  },
  {
    id: 'content/add-content',
    path: 'content/add-content',
    permissions: ['content.post'],
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ContentForm />
      </Suspense>
    ),
  },
  {
    id: 'content/edit-content',
    path: 'content/edit-content/:id',
    permissions: ['content.id.put'],
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ContentForm />
      </Suspense>
    ),
  },
  {
    id: 'products',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <Products />
      </Suspense>
    ),
    path: 'products',
    text: 'layout_route_text_products',
    icon: <AiOutlineShopping size={18} />,
    permissions: ['products.get'],
  },
  {
    id: 'product/detail',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ProductDetail />
      </Suspense>
    ),
    path: 'product/detail/:productId',
    permissions: ['products.get'],
  },
  {
    id: 'orders',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <Orders />
      </Suspense>
    ),
    path: 'orders',
    text: 'layout_route_text_orders',
    icon: <AiOutlineContainer size={18} />,
    permissions: ['orders.get'],
  },
  {
    id: 'orders/detail',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <OrderDetail />
      </Suspense>
    ),
    path: 'orders/detail/:orderId',
    permissions: ['orders.get'],
  },
  {
    id: 'promotions',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PromotionList />
      </Suspense>
    ),
    path: 'promotions',
    text: 'layout_route_text_promotions',
    icon: <RiCoupon2Line size={18} />,
    permissions: ['promotions.get'],
  },
  {
    id: 'promotions/add-promotion',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PromotionAddition />
      </Suspense>
    ),
    path: 'promotions/add-promotion',
    permissions: ['promotions.post'],
  },
  {
    id: 'promotions/add-shopify-promotion/:type',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <AddOrEditShopifyDiscountPage />
      </Suspense>
    ),
    path: 'promotions/add-shopify-promotion/:type',
    permissions: ['promotions.post'],
  },
  {
    id: 'promotions/edit-shopify-promotion/:type',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <AddOrEditShopifyDiscountPage />
      </Suspense>
    ),
    path: 'promotions/edit-shopify-promotion/:type/:id',
    permissions: ['promotions.post'],
  },
  {
    id: 'promotions/edit-promotion/:id',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PromotionEdition />
      </Suspense>
    ),
    path: 'promotions/edit-promotion/:id',
    permissions: ['promotions.id.patch'],
  },
  {
    id: 'promotions/push-promotions/:id',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PushPromotions />
      </Suspense>
    ),
    path: 'promotions/push-promotions/:id',
    permissions: ['promotions.pushes.post'],
  },
  {
    id: 'themes',
    text: 'layout_route_text_themes',
    path: 'themes',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ThemesManagement />
      </Suspense>
    ),
    icon: <AiOutlineTool size={18} />,
    permissions: ['themes.get'],
  },
  {
    id: 'themes/theme-apply/:id',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ThemeApply />
      </Suspense>
    ),
    path: 'themes/theme-apply/:id',
    permissions: ['themes.apply.post'],
  },
  {
    id: 'themes/theme-upload',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ThemeUpload />
      </Suspense>
    ),
    path: 'themes/theme-upload',
    permissions: ['themes.upload.post'],
  },
  {
    id: 'onboarding',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <Onboarding />
      </Suspense>
    ),
    path: 'onboarding',
    text: 'layout_route_text_onboarding',
    permissions: [
      'onboarding.styles.get',
      'onboarding.themes.get',
      'onboarding.themes.post',
    ],
    icon: <AiOutlineShop size={18} />,
  },
  {
    id: 'provisioning-profiles',
    text: 'layout_route_text_provisioning_profiles',
    path: 'provisioning-profiles',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ProvisioningProfiles />
      </Suspense>
    ),
    icon: <AiOutlineShop size={18} />,
    permissions: [
      'provisioning-profiles.get',
      'provisioning-profiles.id.get',
      'provisioning-profiles.id.patch',
    ],
  },
  {
    id: 'provisioning-profiles/apply-profile',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ApplyProfile />
      </Suspense>
    ),
    path: 'provisioning-profiles/apply-profile/:id',
  },
  {
    id: 'provisioning-profiles/create-profile',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <CreateProfile />
      </Suspense>
    ),
    path: 'provisioning-profiles/create-profile',
  },
  {
    id: 'provisioning-profiles/create-shopify-profile',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ProfileShopifyCreate />
      </Suspense>
    ),
    path: 'provisioning-profiles/create-shopify-profile',
  },
  {
    id: 'provisioning-profiles/edit-profile',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <EditProfile />
      </Suspense>
    ),
    path: 'provisioning-profiles/edit-profile/:id',
  },
  {
    id: 'provisioning-profiles/edit-shopify-profile',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ProfileShopifyEdit />
      </Suspense>
    ),
    path: 'provisioning-profiles/edit-shopify-profile/:id',
  },
  {
    id: 'provisioning-profiles/profile-details',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ProfileDetails />
      </Suspense>
    ),
    path: 'provisioning-profiles/profile-details/:id',
    permissions: ['provisioning-profiles.id.get'],
  },
  {
    id: 'provisioning-profiles/shopify-profile-detail',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <ProfileShopifyDetails />
      </Suspense>
    ),
    path: 'provisioning-profiles/shopify-profile-detail/:id',
    permissions: ['provisioning-profiles.id.get'],
  },
  {
    id: 'customer-profiles',
    text: 'layout_route_text_customer_profiles',
    path: 'customer-profiles',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <CustomerManagement />
      </Suspense>
    ),
    icon: <AiOutlineUser size={18} />,
    permissions: ['customers.get'],
  },
  {
    id: 'customer-profiles/customer-details',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <CustomerProfileDetails />
      </Suspense>
    ),
    path: 'customer-profiles/customer-details/:id',
    permissions: ['customers.get'],
  },
  {
    id: 'customer-profiles/customer-order-revenue',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <CustomerOrderRevenue />
      </Suspense>
    ),
    path: 'customer-profiles/customer-order-revenue/:id',
    permissions: ['customers.get', 'revenue.get'],
  },
  {
    id: 'reports',
    text: 'layout_route_text_reports',
    path: 'reports',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <Reports />
      </Suspense>
    ),
    icon: <AiOutlineSnippets size={18} />,
    permissions: ['tenant_logs.get', 'plan-payments.get', 'plan-changes.get'],
  },
  {
    id: 'reports/store-list',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PlanRevenue />
      </Suspense>
    ),
    path: 'reports/store-list',
    permissions: ['plan-payments.get'],
  },
  {
    id: 'reports/portal-logs',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PortalLogs />
      </Suspense>
    ),
    path: 'reports/portal-logs',
    permissions: ['tenant_logs.get'],
  },
  {
    id: 'reports/plans-update',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <LatestPlanChanges />
      </Suspense>
    ),
    path: 'reports/plans-update',
    permissions: ['plan-changes.get'],
  },
  {
    id: 'reports/store-logs',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <StoreLogs />
      </Suspense>
    ),
    path: 'reports/store-logs',
  },
  {
    id: 'reports/orders-revenue',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <OrdersRevenue />
      </Suspense>
    ),
    path: 'reports/orders-revenue',
  },
  {
    id: 'reports/promotion-utilization',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PromotionUtilization />
      </Suspense>
    ),
    path: 'reports/promotion-utilization',
  },
  {
    id: 'reports/system-logs',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <SystemLogs />
      </Suspense>
    ),
    path: 'reports/system-logs',
  },
  {
    id: 'settings',
    text: 'layout_route_text_settings',
    path: 'settings',
    icon: <AiOutlineSetting size={18} />,
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <Settings />
      </Suspense>
    ),
    permissions: ['users.get', 'plans.get', 'groups.get', 'configs.id.get'],
  },
  {
    id: 'settings/portal-user',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PortalUser />
      </Suspense>
    ),
    path: 'settings/portal-user',
    permissions: ['users.get'],
  },
  {
    id: 'settings/plan',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <Plan />
      </Suspense>
    ),
    path: 'settings/plan',
    permissions: ['plans.get'],
  },
  {
    id: 'settings/catalog-setting',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <CatalogSetting />
      </Suspense>
    ),
    path: 'settings/catalog-setting',
    permissions: ['configs.id.get'],
  },
  {
    id: 'settings/master-store',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <MasterStore />
      </Suspense>
    ),
    path: 'settings/master-store',
    permissions: ['configs.id.get'],
  },
  {
    id: 'settings/group',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <Group />
      </Suspense>
    ),
    path: 'settings/group',
    permissions: ['groups.get'],
  },
  {
    id: 'settings/automation-notification',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <AutomationNotification />
      </Suspense>
    ),
    path: 'settings/automation-notification',
    permissions: ['configs.id.get'],
  },
  {
    id: 'settings/api-franchise-token',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <APIFranchiseToken />
      </Suspense>
    ),
    path: 'settings/api-franchise-token',
    permissions: ['configs.id.get'],
  },
  {
    id: 'settings/sms-gateway-config',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <SMSGatewayConfig />
      </Suspense>
    ),
    path: 'settings/sms-gateway-config',
  },
  {
    id: 'settings/sms-template',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <SMSTemplate />
      </Suspense>
    ),
    path: 'settings/sms-template',
  },
  {
    id: 'settings/smtp-setting',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <SMTPSetting />
      </Suspense>
    ),
    path: 'settings/smtp-setting',
  },
  {
    id: 'settings/mail-template',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <MailTemplate />
      </Suspense>
    ),
    path: 'settings/mail-template',
    permissions: ['email-templates.get', 'email-templates.put'],
  },
  // {
  //   id: 'support',
  //   element: <Support />,
  //   path: 'support',
  //   text: 'Support',
  //   icon: <AiOutlineContacts size={18} />,
  // },
  {
    id: 'not-found',
    path: '*',
    element: <Navigate to="/login" />,
  },
]

export const pageTypes = {
  noFrame: [
    '',
    'login',
    'reset-password',
    'forgot-password',
    'add-account-logo-title',
  ],
}
